import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#applySchedule();
    this.play();
  }

  /**
   * Hides announcements outside their scheduled start/end dates.
   * Runs in the browser so the result doesn't depend on Shopify's page cache.
   */
  #applySchedule() {
    const now = Date.now();

    for (const slide of this.refs.slides ?? []) {
      const { scheduleStart, scheduleEnd } = slide.dataset;
      if (scheduleStart === undefined && scheduleEnd === undefined) continue;

      const start = this.#parseScheduleDate(scheduleStart, false);
      const end = this.#parseScheduleDate(scheduleEnd, true);

      slide.hidden = (start !== null && now < start) || (end !== null && now > end);
    }

    const visibleCount = this.#visibleSlides.length;

    if (visibleCount === 0) {
      (this.closest('.shopify-section') ?? this).setAttribute('hidden', '');
      return;
    }

    if (visibleCount === 1) {
      this.removeAttribute('autoplay');
      this.removeAttribute('aria-live');
      this.querySelector('slideshow-arrows')?.remove();
    }

    this.current = 0;
  }

  /**
   * Parses "DD/MM/AAAA HH:MM" (or "AAAA-MM-DD HH:MM") in the store's timezone.
   * @param {string | undefined} value
   * @param {boolean} isEnd - Whether the date marks the end of the period (rounds up to the end of the minute/day).
   * @returns {number | null} The timestamp in milliseconds, or null when empty/invalid.
   */
  #parseScheduleDate(value, isEnd) {
    const text = value?.trim();
    if (!text) return null;

    let match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
    let day, month, year;

    if (match) {
      [, day, month, year] = match;
    } else {
      match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[\sT]+(\d{1,2}):(\d{2}))?$/);
      if (match) [, year, month, day] = match;
    }

    if (!match) {
      console.warn(`Announcement bar: invalid schedule date "${text}". Use DD/MM/AAAA HH:MM.`);
      return null;
    }

    const hasTime = match[4] !== undefined;
    const hours = hasTime ? Number(match[4]) : isEnd ? 23 : 0;
    const minutes = hasTime ? Number(match[5]) : isEnd ? 59 : 0;
    const seconds = isEnd ? 59 : 0;
    const milliseconds = isEnd ? 999 : 0;

    const utc = Date.UTC(Number(year), Number(month) - 1, Number(day), hours, minutes, seconds, milliseconds);
    const check = new Date(utc);

    if (
      check.getUTCDate() !== Number(day) ||
      check.getUTCMonth() !== Number(month) - 1 ||
      check.getUTCHours() !== hours ||
      check.getUTCMinutes() !== minutes
    ) {
      console.warn(`Announcement bar: invalid schedule date "${text}".`);
      return null;
    }

    return utc - this.#timezoneOffsetMinutes * 60 * 1000;
  }

  /**
   * The store's timezone offset in minutes, from the "+HHMM"/"-HHMM" value rendered by Liquid.
   * @returns {number}
   */
  get #timezoneOffsetMinutes() {
    const match = (this.dataset.timezoneOffset ?? '').match(/^([+-])(\d{2})(\d{2})$/);
    if (!match) return -new Date().getTimezoneOffset();

    const [, sign, hours, minutes] = match;
    return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes));
  }

  /**
   * Slides that are not hidden by their schedule.
   * @returns {HTMLElement[]}
   */
  get #visibleSlides() {
    return (this.refs.slides ?? []).filter((slide) => !slide.hidden);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.#visibleSlides;
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    this.refs.slides?.forEach((slide) => {
      slide.setAttribute('aria-hidden', `${slide !== slides[relativeIndex]}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
